<script setup lang="ts">
import type { ColumnDef, ColumnFiltersState, SortingState, VisibilityState } from '@tanstack/vue-table'
import { useRouter } from 'vue-router'
import { Member } from '@/types/Member'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

const router = useRouter()
import { valueUpdater } from '@/lib/utils'
import {
	FlexRender, getCoreRowModel, getFacetedRowModel, getFacetedUniqueValues,
	getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useVueTable,
} from '@tanstack/vue-table'
import { ref, watch } from 'vue'
import DataTablePagination from '@/components/DataTablePagination.vue'
import UserTableFilter from '@/components/UserTableFilter.vue'

interface DataTableProps {
	columns: ColumnDef<Member, any>[]
	data: Member[]
	initialColumnVisibility?: VisibilityState
}
const props = defineProps<DataTableProps>()

const emit = defineEmits<{
	(e: 'update:columnVisibility', value: VisibilityState): void
}>()

const sorting = ref<SortingState>([])
const columnFilters = ref<ColumnFiltersState>([])
const columnVisibility = ref<VisibilityState>(props.initialColumnVisibility || {})
const rowSelection = ref({})

const table = useVueTable({
	get data() { return props.data },
	get columns() { return props.columns },
	state: {
		get sorting() { return sorting.value },
		get columnFilters() { return columnFilters.value },
		get columnVisibility() { return columnVisibility.value },
		get rowSelection() { return rowSelection.value },
	},
	enableRowSelection: true,
	onSortingChange: updaterOrValue => valueUpdater(updaterOrValue, sorting),
	onColumnFiltersChange: updaterOrValue => valueUpdater(updaterOrValue, columnFilters),
	onColumnVisibilityChange: (updaterOrValue) => {
		valueUpdater(updaterOrValue, columnVisibility)
		emit('update:columnVisibility', columnVisibility.value)
	},
	onRowSelectionChange: updaterOrValue => valueUpdater(updaterOrValue, rowSelection),
	getCoreRowModel: getCoreRowModel(),
	getFilteredRowModel: getFilteredRowModel(),
	getPaginationRowModel: getPaginationRowModel(),
	getSortedRowModel: getSortedRowModel(),
	getFacetedRowModel: getFacetedRowModel(),
	getFacetedUniqueValues: getFacetedUniqueValues(),
})
</script>

<template>
	<div class="space-y-4">
		<UserTableFilter :table="table" />
		<div class="border rounded-md">
			<Table>
				<TableHeader>
				<TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
				<TableHead v-for="header in headerGroup.headers" :key="header.id">
				<FlexRender v-if="!header.isPlaceholder" :render="header.column.columnDef.header" :props="header.getContext()" />
				</TableHead>
				</TableRow>
				</TableHeader>
				<TableBody>
				<template v-if="table.getRowModel().rows?.length">
					<TableRow
							v-for="row in table.getRowModel().rows"
							:key="row.id"
							:data-state="row.getIsSelected() && 'selected'"
							class="cursor-pointer hover:bg-muted/50"
							@click="router.push('/member/' + row.original.id)"
							>
							<TableCell v-for="cell in row.getVisibleCells()" :key="cell.id">
							<FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
							</TableCell>
					</TableRow>
				</template>

				<TableRow v-else>
				<TableCell
						:colspan="columns.length"
						class="h-24 text-center"
						>
						No results.
				</TableCell>
				</TableRow>
				</TableBody>
			</Table>
		</div>
		<DataTablePagination :table="table" />
	</div>
</template>
